import { Color } from '@persona-id/design-tokens';
import { useMemo } from 'react';
import styled from 'styled-components';

import BarAndLineChartWithLegendV2 from 'dashboard/components/BarAndLineChartV2';
import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import { Period } from 'dashboard/lib/unixTime';
import { fillChartData, generateDomain } from 'dashboard/lib/utils/charting';
import { formatChartDate, formatChartDateHourChart } from 'dashboard/lib/utils/formatters';
import {
  MetricsApiLogVolumeDatum,
  MetricsApiLogVolumeOverTime,
  MetricsApiLogVolumeRates,
} from 'dashboard/models/metrics-api-logs';
import Card from 'design-system/components/Card';

const COLORS = [Color.CoreTeal100, Color.CorePurple800, Color.CoreOrange700];

const Container = styled.section``;

export interface ApiMetricsProps {
  isLoading: boolean;
  volumeOverTime: MetricsApiLogVolumeOverTime;
}

const calculatePercentage = (numerator: number, denominator: number): number | null => {
  if (denominator === 0) {
    return null;
  }
  return (numerator * 100) / denominator;
};

const augmentWithRates = (
  data: MetricsApiLogVolumeDatum[],
): (MetricsApiLogVolumeDatum & MetricsApiLogVolumeRates)[] => {
  return data.map((datum) => {
    return {
      ...datum,
      success_rate: calculatePercentage(datum.success_volume, datum.requests),
      client_error_rate: calculatePercentage(datum.client_error_volume, datum.requests),
    };
  });
};

const ApiMetrics = ({ isLoading, volumeOverTime }: ApiMetricsProps) => {
  const filledData = useMemo(() => {
    const data = augmentWithRates(volumeOverTime.data);
    return fillChartData(
      data,
      {
        requests: 0,
        success_volume: 0,
        client_error_volume: 0,
        success_rate: 0,
        client_error_rate: 0,
      },
      generateDomain(volumeOverTime.domain, volumeOverTime.period),
      volumeOverTime.period,
      false,
    );
  }, [volumeOverTime.data, volumeOverTime.domain, volumeOverTime.period]);

  return (
    <Container>
      <Card>{isLoading && <LoadingOverlay />}</Card>
      <Card>
        <Card.Body>
          <Card.Title>API Usage</Card.Title>
          <BarAndLineChartWithLegendV2
            data={filledData}
            colors={COLORS}
            barKeys={['requests']}
            barsLabel='Traffic'
            lineKeys={['success_rate', 'client_error_rate']}
            linesLabel='Response Status'
            initialFilteredLegendKeys={[]}
            createTooltipHeader={() => ''}
            xDataKey='created_timestamp'
            xFormatter={
              volumeOverTime.period === Period.Hour ? formatChartDateHourChart : formatChartDate
            }
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ApiMetrics;
